import * as React from "react";
import { Button, ClickAwayListener, MenuItem, MenuList, Paper, Popper, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import i18n from "src/common/i18n";
import { IconArrowDropDown } from "src/common/icons";
import { modifyLocales } from "src/helpers";

const useStyles = makeStyles({
  button: {
    "& .icon-arrow-drop-down": {
      fontSize: "1.1rem",
      marginLeft: "6px"
    },
    fontFamily: "Roboto, Arial, Helvetica ,sans-serif",
    fontSize: "1.4rem"
  },
  languagePopper: {
    borderRadius: "2px",
    boxShadow: "0 2px 4px 0 rgba(189, 189, 189, 0.5)",
    left: "-3px !important ",
    top: "-5px !important ",
    width: "auto",
    zIndex: 1500
  },
  listItem: {
    backgroundColor: "#fff !important",
    fontSize: 14,
    height: 9,
    padding: 16
  },
  selectedLanguage: {
    color: "#e77d33"
  }
});

interface IOwnProps {
  lngs: string[];
  location: {
    pathname: string;
    search: string;
  };
  history: {
    push: (arg0: string) => void;
    replace: (arg0: string) => void;
  };
}

interface IState {
  open: boolean;
  anchor: null | Element;
}

const Languages = (props: IOwnProps) => {
  const {
    i18n: { language }
  } = useTranslation();
  const classes = useStyles();
  const { lngs, location, history } = props;
  const [{ open, anchor }, updateState] = React.useState<IState>({
    anchor: null,
    open: false
  });

  const pathnameMatch = location.pathname.match(/[^/?]*[^/?]/);
  const pathnameLanguage = pathnameMatch !== null ? pathnameMatch[0] : null;

  // set document language dynamically after refresh
  React.useEffect(() => {
    if (pathnameLanguage !== null && lngs.includes(pathnameLanguage) && pathnameLanguage !== language) {
      document.documentElement.lang = pathnameLanguage;
    }
  }, []);
  React.useEffect(() => {
    if (pathnameLanguage !== null && lngs.includes(pathnameLanguage) && pathnameLanguage !== language) {
      i18n.changeLanguage(pathnameLanguage);
      localStorage.setItem("selectedLanguage", pathnameLanguage);
    }
  }, [pathnameLanguage, language, lngs]);

  const closeDropDown = () => updateState({ open: false, anchor: null });
  const openDropDown = ({ currentTarget }: React.MouseEvent) => updateState({ open: true, anchor: currentTarget });

  const changeLng = (newLng: string) => (e: React.MouseEvent) => {
    i18n.changeLanguage(newLng);
    localStorage.setItem("selectedLanguage", newLng);
    document.documentElement.lang = newLng;
    const newLocation = location.pathname.replace(`/${language}`, `/${newLng}`) + location.search;

    history.replace(newLocation);
    closeDropDown();
  };

  return (
    <React.Fragment>
      <Button className={classes.button} color="inherit" onClick={open ? closeDropDown : openDropDown}>
        {language} <IconArrowDropDown />
      </Button>
      <Popper open={open} disablePortal anchorEl={anchor} className={classes.languagePopper}>
        <Paper>
          <ClickAwayListener onClickAway={closeDropDown}>
            <MenuList>
              {lngs.map(otherLng => {
                const selected = language === otherLng;
                return (
                  <MenuItem
                    className={selected ? [classes.listItem, classes.selectedLanguage].join(" ") : classes.listItem}
                    selected={language === otherLng}
                    onClick={selected ? undefined : changeLng(otherLng)}
                    key={otherLng}
                  >
                    {modifyLocales(otherLng)}
                  </MenuItem>
                );
              })}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </React.Fragment>
  );
};

export default React.memo(Languages);
